import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { connect } from "react-redux";
import { IconButton, Tooltip } from "@material-ui/core";
import { ArrowBack as ArrowBackIcon } from "@material-ui/icons";
import ReactPlayer from "react-player";
import VideoControls from "./components/VideoControls/index";
import VideoList from "./components/VideoList/index";
import { SpeedTest } from "./utils/SpeedTest";
import { fetchVideos } from "./utils/api";
import ReportModal from "./components/Report/ReportModal";
import ReportCartModal from "./components/Report/ReportCartModal";
import PageHeader from "components/layout/page-header/PageHeader";
import api from "api";
import "./styles.css";

const VideoPlayer = ({ token, isPublic = false }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { project, date, folder_path: folderPathFromState } = location.state || {};
  const [videos, setVideos] = useState([]);
  const [currentVideo, setCurrentVideo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedQuality, setSelectedQuality] = useState("auto");
  const [suggestedQuality, setSuggestedQuality] = useState(null);
  const [availableQualities, setAvailableQualities] = useState([]);
  const [connectionSpeed, setConnectionSpeed] = useState(null);
  const [showReportModal, setShowReportModal] = useState(false);
  const [showReportCart, setShowReportCart] = useState(false);
  const [screenshot, setScreenshot] = useState(null);
  const [showCaptureHint, setShowCaptureHint] = useState(false);
  const hintTimerRef = useRef(null);
  const [controls, setControls] = useState(true);
  const [playing, setPlaying] = useState(false);
  const [projectName, setProjectName] = useState(project?.name || "");
  const [projectDate, setProjectDate] = useState(date || "");
  const playerRef = useRef(null);

  const handleSelectVideo = video => {
    setCurrentVideo(video);
    setPlaying(true);
  };

  const shareToken = useMemo(() => {
    if (!isPublic) return null;
    const search = new URLSearchParams(location.search);
    return search.get("token");
  }, [isPublic, location.search]);

  useEffect(() => {
    if (!isPublic) {
      setProjectName(project?.name || "");
      setProjectDate(date || "");
    }
  }, [isPublic, project, date]);

  const inferProjectInfo = file => {
    if (!file) return {};
    const source = file.path || file.key || file.url;
    if (!source) return {};

    try {
      const base =
        typeof window !== "undefined" && window.location
          ? window.location.origin
          : undefined;
      const parsedUrl = base ? new URL(source, base) : new URL(source);
      const segments = parsedUrl.pathname.split("/").filter(Boolean);
      const projetosIndex = segments.findIndex(
        segment => segment === "projetos"
      );

      if (projetosIndex !== -1 && segments.length > projetosIndex + 3) {
        const typeSegment = segments[projetosIndex + 1];
        if (typeSegment === "filmagens") {
          return {
            name: decodeURIComponent(segments[projetosIndex + 2] || ""),
            date: decodeURIComponent(segments[projetosIndex + 3] || "")
          };
        }
      }
    } catch (error) {
      console.warn(
        "Não foi possível inferir informações do projeto a partir da URL compartilhada.",
        error
      );
    }

    return {};
  };

  useEffect(() => {
    const testConnection = async () => {
      const speed = await SpeedTest();
      setConnectionSpeed(speed);
      const quality = speed < 2 ? "360p" : speed < 5 ? "480p" : "720p";
      setSuggestedQuality(quality);
    };

    testConnection();
  }, []);

  useEffect(() => {
    if (isPublic && !shareToken) {
      setError("Token público não fornecido.");
      setLoading(false);
      return;
    }

    if (!isPublic && (!project?.name || !date || !token)) {
      setError("Projeto, data ou token não fornecidos.");
      setLoading(false);
      return;
    }

    const abortController = new AbortController();
    const loadVideos = async () => {
      try {
        setLoading(true);
        let videoFiles;

        if (isPublic) {
          let response;
          try {
            response = await api.get(
              `${process.env.REACT_APP_API_URL}/projects/shared/files/${shareToken}`,
              { signal: abortController.signal }
            );
          } catch (error) {
            setError(
              error.response?.status === 404
                ? "Token não encontrado ou inválido."
                : error.response?.status === 403
                ? "Token expirado."
                : `Erro ao buscar vídeos: ${error.message ||
                    "Erro desconhecido"}`
            );
            return;
          }

          if (!response.data) {
            setError("Resposta inválida do servidor. Tente novamente.");
            return;
          }

          const responseFiles = response.data.files;

          if (
            !responseFiles ||
            !Array.isArray(responseFiles) ||
            responseFiles.length === 0
          ) {
            setError("Nenhum vídeo encontrado. [ 53 ]");
            return;
          }

          videoFiles = responseFiles
            .filter(
              video => video.original || video.url || video.path || video.key
            )
            .map(video => {
              const mainUrl =
                video.original || video.url || video.path || video.key;
              return {
                ...video,
                original: video.original || mainUrl,
                url: mainUrl,
                thumbnail: video.thumbnail || null
              };
            });

          if (videoFiles.length > 0) {
            const info = inferProjectInfo(videoFiles[0]);
            if (info.name) {
              setProjectName(info.name);
            }
            if (info.date) {
              setProjectDate(info.date);
            }
          }
        } else {
          const folderPath = folderPathFromState || `projetos/${project.name}/${date}/filmagens`;
          videoFiles = await fetchVideos(folderPath, token, {
            signal: abortController.signal
          });
        }

        if (
          !videoFiles ||
          !Array.isArray(videoFiles) ||
          videoFiles.length === 0
        ) {
          setError("Nenhum vídeo encontrado.");
          return;
        }

        const validVideos = videoFiles.filter(
          video => video.original || video.url || video.path || video.key
        );

        if (validVideos.length === 0) {
          setError("Nenhum vídeo válido encontrado. [ 60 ]");
          return;
        }

        const processedVideos = validVideos.map(video => {
          const videoUrl = video.url || video.path || video.key;
          return {
            ...video,
            original: video.original || videoUrl,
            url: videoUrl,
            qualities: video.variants
              ? ["original", ...video.variants.map(v => v.quality)]
              : ["original"]
          };
        });

        setVideos(processedVideos);
        setCurrentVideo(processedVideos[0]);
        setAvailableQualities(processedVideos[0].qualities);
        setControls(true);
      } catch (err) {
        if (err.name !== "AbortError") {
          console.error("Erro ao buscar vídeos:", err);
          setError("Erro ao carregar os vídeos.");
        }
      } finally {
        if (!abortController.signal.aborted) {
          setLoading(false);
        }
      }
    };

    loadVideos();
    return () => abortController.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project, date, token, isPublic, shareToken]);

  useEffect(() => {
    if (currentVideo?.qualities) {
      setAvailableQualities(currentVideo.qualities);
    }
  }, [currentVideo]);

  const getVideoUrl = () => {
    if (!currentVideo) return null;

    const qualityToUse =
      selectedQuality === "auto" ? suggestedQuality : selectedQuality;

    if (
      !currentVideo.qualities ||
      !currentVideo.qualities.includes(qualityToUse)
    ) {
      return currentVideo.original || currentVideo.url;
    }

    if (qualityToUse === "original") {
      return currentVideo.original || currentVideo.url;
    }

    const found = currentVideo.variants?.find(v => v.quality === qualityToUse);
    return found?.url || currentVideo.original || currentVideo.url;
  };

  const handleTakeScreenshot = () => {
    if (isPublic) return;
    if (!playerRef.current) return;

    playerRef.current.getInternalPlayer().pause();
    const videoElement = playerRef.current.getInternalPlayer();
    const canvas = document.createElement("canvas");
    canvas.width = videoElement.videoWidth;
    canvas.height = videoElement.videoHeight;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(videoElement, 0, 0, canvas.width, canvas.height);

    setScreenshot(canvas.toDataURL("image/png"));
    setShowReportModal(true);
  };

  const handleOpenReportCart = () => {
    if (isPublic) return;
    setShowReportCart(true);
  };

  const handleOccurrenceAdded = useCallback(() => {
    if (hintTimerRef.current) clearTimeout(hintTimerRef.current);
    setShowCaptureHint(true);
    hintTimerRef.current = setTimeout(() => setShowCaptureHint(false), 5000);
  }, []);

  if (loading) return <div className="loading-spinner">Carregando...</div>;
  if (error) return <div className="error-message">{error}</div>;

  const galleryTitle =
    (projectName || "Galeria de vídeos") +
    (projectDate ? ` — ${projectDate}` : "");

  const handleBack = () => {
    if (isPublic) {
      navigate(-1);
    } else {
      navigate("/projects", { state: { openProject: project?.name } });
    }
  };

  const backButton = (
    <Tooltip title="Voltar">
      <IconButton
        aria-label="Voltar"
        onClick={handleBack}
        size="small"
        style={{
          border: "1px solid rgba(0,0,0,0.12)",
          color: "rgba(0,0,0,0.6)"
        }}
      >
        <ArrowBackIcon />
      </IconButton>
    </Tooltip>
  );

  return (
    <div className="video-hub-container">
      <PageHeader
        accent="Galeria de vídeos"
        title={galleryTitle}
        subtitle={projectName ? "Visualização dos vídeos do projeto" : undefined}
        leftAdornment={backButton}
      />

      <div className="video-content">
        <div className="video-player-wrapper">
          {currentVideo && getVideoUrl() && (
            <ReactPlayer
              key={currentVideo?.original}
              preload="metadata"
              ref={playerRef}
              url={getVideoUrl()}
              playing={playing}
              controls={controls}
              volume={0.8}
              width="100%"
              height="91%"
              config={{
                file: {
                  attributes: { crossOrigin: "anonymous" }
                }
              }}
              onPlay={() => setPlaying(true)}
              onPause={() => setPlaying(false)}
              onEnded={() => setPlaying(false)}
              onError={error => {
                console.error("ReactPlayer error:", error);
                setError(
                  "Erro ao carregar o vídeo. Verifique se a URL é válida. [ 61 ]"
                );
              }}
            />
          )}
          {currentVideo && !getVideoUrl() && (
            <div className="error-message">
              URL do vídeo inválida. Verifique os logs do console.
            </div>
          )}

          {!isPublic && (
            <VideoControls
              onScreenshot={handleTakeScreenshot}
              onReportCart={handleOpenReportCart}
              qualities={availableQualities}
              selectedQuality={selectedQuality}
              onSelectQuality={setSelectedQuality}
              connectionSpeed={connectionSpeed}
              showCaptureHint={showCaptureHint}
              onDismissHint={() => setShowCaptureHint(false)}
            />
          )}
        </div>

        <VideoList
          videos={videos}
          currentVideo={currentVideo}
          onSelectVideo={handleSelectVideo}
        />
      </div>

      {!isPublic && showReportModal && (
        <ReportModal
          screenshot={screenshot}
          onClose={() => {
            setShowReportModal(false);
            // eslint-disable-next-line no-unused-expressions
            playerRef.current?.getInternalPlayer()?.play();
          }}
          onAdded={handleOccurrenceAdded}
        />
      )}

      {!isPublic && showReportCart && (
        <ReportCartModal
          onClose={() => setShowReportCart(false)}
          folderPath={folderPathFromState || `projetos/${projectName || project?.name}/${projectDate || date}/filmagens`}
          token={token}
        />
      )}
    </div>
  );
};

const mapStateToProps = state => ({
  token: state.auth.accessToken
});

export default connect(mapStateToProps)(VideoPlayer);
